import React, { Dispatch, SetStateAction, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link } from "react-router-dom";
import DeletePopUp from "../DeletePopUp";
import { ICooperative } from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { fetchCooperative } from "../../api/DashboardApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { darkenColor } from "../../exports/functions";
import { usePermission } from "../../hooks/usePermission";

const CooperativeTable = ({
  cooperative,
  setCooperative,
}: {
  cooperative: ICooperative[];
  setCooperative: Dispatch<SetStateAction<ICooperative[]>>;
}) => {
  type Cooperative = {
    id: string;
    name: string;
    pan: string;
    email: string;
    mobile: string;
    status: string;
    nature: string;
  };

  const { statusOptions } = useOption();

  const data: Cooperative[] = Array.from({ length: 10 }).map((_, i) => ({
    id: "0000" + (i + 1),
    name: "Patan Agricultural Cooperative",
    pan: "1234567890",
    email: "cooperative@xxx.com.np",
    mobile: "9841-123-123",
    status: i % 3 === 0 ? "Active" : i % 3 === 1 ? "Inactive" : "Deactivated",
    nature:
      i % 4 === 0
        ? "Multipurpose"
        : i % 4 === 1
          ? "Saving and Credits"
          : i % 4 === 2
            ? "Agricultural"
            : "Others",
  }));

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Cooperative Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const natureClass = (nature: string) => {
    switch (nature) {
      case "Multipurpose":
        return "pill-info";
      case "Saving and Credit":
        return "pill-danger";
      case "Agricultural":
        return "pill-success";
      case "Others":
        return "pill-primary";
      default:
        return "";
    }
  };
  const { setLoading } = useLoader();
  const { canEdit, canDelete } = usePermission();
  const isMobile = useMobile();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = cooperative.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(cooperative.length / itemsPerPage);
  const handleClose = () => {
    setShowDelete(false);
  };
  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/registers/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchCooperative()
          .then((cooperativeData) => {
            setCooperative(cooperativeData);
            setShowDelete(false);
            setDeleteId(null);
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Registered cooperative deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            setTimeout(() => {
              setLoading(false);
              dynamicFailed(`${error.message}`);
            }, 1000);
          });
      }
    } catch (error: any) {
      setTimeout(() => {
        dynamicFailed(`${error.message}`);
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="dashboard-card">
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th></th>
                <th>Reg no.</th>
                <th>Name</th>
                <th>Reg. PAN no.</th>
                <th>Email Address</th>
                <th>Mobile no.</th>
                <th>Status</th>
                <th>Nature of Work</th>
                <th className="sticky-action">Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentData.map((item, i) => (
                <tr key={i}>
                  <td>
                    <input type="checkbox" />
                  </td>

                  <td className="text-danger">{item.registrationNumber}</td>
                  <td>{item.nameEnglish}</td>
                  <td>{item.panNumber}</td>
                  <td>{item.email}</td>
                  <td>{item.mobileNumber}</td>

                  <td>
                    <span
                      className={`badge rounded-pill px-3 py-2`}
                      style={statusStyle(data[0].status)}
                    >
                      {data[0].status}
                    </span>
                  </td>

                  <td>
                    <span
                      className={`badge rounded-pill px-3 py-2 ${natureClass(item.natureOfWork)}`}
                    >
                      {item.natureOfWork}
                    </span>
                  </td>

                  <td className="sticky-action">
                    <div className="d-flex gap-2 align-items-center">
                      <Link to={`/cooperatives-list/${item.id}`}>
                        <i
                          className="cursor-pointer"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="View"
                        >
                          <Eye />
                        </i>
                      </Link>
                      <Link
                        to={
                          canEdit("cooperatives")
                            ? `/register/edit/${item.id}`
                            : "#"
                        }
                        onClick={(e) => {
                          if (!canEdit("cooperatives")) {
                            e.preventDefault();
                          }
                        }}
                        style={{
                          pointerEvents: canEdit("cooperatives")
                            ? "auto"
                            : "none",
                          opacity: canEdit("cooperatives") ? 1 : 0.5,
                        }}
                      >
                        <i
                          data-toggle="tooltip"
                          data-placement="top"
                          title={
                            canEdit("cooperatives") ? "Edit" : "No permission"
                          }
                        >
                          <Pencil size={20} />
                        </i>
                      </Link>
                      <i
                        className={`fa-solid fa-trash text-danger ${
                          canDelete("cooperatives")
                            ? "cursor-pointer"
                            : "opacity-50"
                        }`}
                        data-toggle="tooltip"
                        data-placement="top"
                        title={
                          canDelete("cooperatives") ? "Delete" : "No permission"
                        }
                        style={{
                          pointerEvents: canDelete("cooperatives")
                            ? "auto"
                            : "none",
                        }}
                        onClick={() => {
                          if (!canDelete("cooperatives")) return;

                          setDeleteId(item.id);
                          setShowDelete(true);
                        }}
                      ></i>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div
          className={`d-flex justify-content-between align-items-center flex-wrap ${isMobile ? "gap-2" : ""}`}
        >
          <small>
            {indexOfFirstItem + 1}–
            {Math.min(indexOfLastItem, cooperative.length)} of{" "}
            {cooperative.length} Registration
          </small>

          <div className="pagination">
            <button
              className="pagination-arrow"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              <i className="fa-solid fa-angle-left"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-page ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="pagination-arrow"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              <i className="fa-solid fa-angle-right"></i>
            </button>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default CooperativeTable;
